import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, MotionValue, motion, useMotionTemplate, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Logo } from "../Logo";
import { WorkCard } from "./WorkCard";
import { useScrollIdle } from "../../hooks/useScrollIdle";
import { workItems } from "../../data/portfolio";
import { stageHeading } from "../../utils/typography";
import type { CollageWorkItem, WorkItem } from "../../types/work";
type WorkCollageProps = {
  onOpen: (item: WorkItem) => void;
};

/** A restrained head start preserves the editorial stagger without creating dead zones. */
const columnOffsets = ["lg:mt-0", "lg:mt-[4rem]", "lg:mt-[2rem]"];
function CollageItem({
  item,
  index,
  progress,
  scrolling,
  onOpen
}: {
  item: CollageWorkItem;
  index: number;
  progress: MotionValue<number>;
  scrolling: boolean;
  onOpen: (item: WorkItem) => void;
}) {
  const reduceMotion = useReducedMotion();
  const drift = reduceMotion ? 0 : item.position.drift;
  const y = useTransform(progress, [0, 1], [drift, -drift]);
  return <motion.div initial={{
    opacity: 0,
    y: 46
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-12%"
  }} transition={{
    duration: 0.8,
    delay: Math.min(index * 0.06, 0.3),
    ease: [0.16, 1, 0.3, 1]
  }} className="relative w-full" data-id="element-539" data-mp="11jd8rz2e3o4w">
      <motion.div style={{
      y
    }} data-id="element-540" data-mp="rrhdevrouc4d">
        <WorkCard item={item} scrolling={scrolling} onOpen={onOpen} fitMedia data-id="element-541" data-mp="1fimqj368uqaz" />
      </motion.div>
    </motion.div>;
}
export function WorkCollage({
  onOpen
}: WorkCollageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const framesRef = useRef<HTMLDivElement>(null);
  const [framed, setFramed] = useState(false);
  const scrolling = useScrollIdle();
  const reduceMotion = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: stageRef,
    offset: ["start start", "end start"]
  });
  const blurAmount = useTransform(scrollYProgress, [0, 0.08, 0.26], [0, 3, 12]);
  const titleFilter = useMotionTemplate`blur(${blurAmount}px)`;
  const titleOpacity = useTransform(scrollYProgress, [0, 0.1, 0.3], [1, 0.9, 0.42]);
  const titleScale = useTransform(scrollYProgress, [0, 0.4], [1, 1.09]);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.06], [1, 0]);
  const {
    scrollYProgress: driftProgress
  } = useScroll({
    target: stageRef,
    offset: ["start end", "end start"]
  });
  const columns = useMemo(() => [0, 1, 2].map(column => workItems.filter(item => item.position.column === column)), []);

  // Rails track the frame grid, not the whole stage, so they never duplicate the title's own label.
  useEffect(() => {
    const node = framesRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => setFramed(entry.isIntersecting), {
      rootMargin: "-25% 0px -25% 0px"
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <div ref={stageRef} className="relative" data-id="element-542" data-mp="2zftthkwx0x1o">
      <AnimatePresence data-id="element-543" data-mp="l7kmxk768dx0">
        {framed && <motion.div aria-hidden="true" className="pointer-events-none fixed inset-x-5 top-8 z-40 hidden items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-white/55 md:inset-x-8 md:flex" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-544" data-mp="l6rm0zuw8ims">
            <span className="flex items-center gap-4" data-id="element-545" data-mp="29gn5gd6br7v">004<span className="h-px w-14 bg-white/25" data-id="element-546" data-mp="2ajaxff79djk" />Selected work</span>

            <span className="flex items-center gap-2.5" data-id="element-547" data-mp="78kvk4s9xkx5">
              <Logo className="h-[18px] w-[40px]" data-id="element-548" data-mp="b9mwqr5r471r" />
              <span className="text-white/45" data-id="element-549" data-mp="b7tpb9nbdisp">+</span>
            </span>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-550" data-mp="g63khk91gjeb">
        {framed && <motion.div aria-hidden="true" className="pointer-events-none fixed inset-x-8 bottom-28 z-40 hidden items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-white/50 md:flex" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-551" data-mp="1mnibhysqudsg">
            <span className="flex gap-5" data-id="element-552" data-mp="1z75wfk2284ka"><span data-id="element-553" data-mp="1z7e7d6emgzsu">LinkedIn</span><span data-id="element-554" data-mp="1z7xeds2qi3s3">WhatsApp</span><span data-id="element-555" data-mp="1z8gzfhnol8ac">Email</span></span>
            <span className="flex items-center gap-2" data-id="element-556" data-mp="1v5smoyvujh83"><span className="h-1.5 w-1.5 rounded-full bg-[#ff5f56] rec-pulse" data-id="element-557" data-mp="1v4rer3a4naul" />REC</span>
          </motion.div>}
      </AnimatePresence>

      {/* Stage one: the title holds the full frame on its own. */}
      <div className="sticky top-0 z-0 flex h-[100svh] flex-col items-center justify-center overflow-hidden px-5 text-center" data-id="element-558" data-mp="245yxue42o543">
        <div aria-hidden="true" className="pointer-events-none absolute inset-6 hidden md:block" data-id="element-559" data-mp="2iwy6tq8tkvo7">
          <span className="absolute left-0 top-0 h-7 w-px bg-white/15" data-id="element-560" data-mp="2dyeijj6r77so" /><span className="absolute left-0 top-0 h-px w-7 bg-white/15" data-id="element-561" data-mp="2rwcrua67we1b" />
          <span className="absolute right-0 top-0 h-7 w-px bg-white/15" data-id="element-562" data-mp="2mxi20o9u1p7p" /><span className="absolute right-0 top-0 h-px w-7 bg-white/15" data-id="element-563" data-mp="36iijjo4j9j7e" />
          <span className="absolute bottom-0 left-0 h-7 w-px bg-white/15" data-id="element-564" data-mp="36jbfrv5v8qba" /><span className="absolute bottom-0 left-0 h-px w-7 bg-white/15" data-id="element-565" data-mp="3bgk1g5q83cha" />
          <span className="absolute bottom-0 right-0 h-7 w-px bg-white/15" data-id="element-566" data-mp="2xi3t6acppdx1" /><span className="absolute bottom-0 right-0 h-px w-7 bg-white/15" data-id="element-567" data-mp="2xjwr9199eptf" />
        </div>

        <motion.div style={reduceMotion ? undefined : {
        filter: titleFilter,
        opacity: titleOpacity,
        scale: titleScale
      }} className="flex flex-col items-center" data-id="element-568" data-mp="31hm72m648oqd">
          <p className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-569" data-mp="2skbvt0taszlg">
            <span className="h-px w-10 bg-[#54f6c6]/50" data-id="element-570" data-mp="2ds06bua3i1g9" />004 / Selected work
          </p>
          <h2 id="work-heading" className={stageHeading} data-id="element-571" data-mp="2iquyvnxv74dk">
            Selected <span className="text-[#54f6c6]" data-id="element-572" data-mp="24uhheetbz2z3">work</span>
          </h2>
          <p className="mx-auto mt-10 max-w-2xl text-[13px] leading-7 text-white/60 sm:text-sm md:text-[15px] md:leading-8 lg:max-w-none lg:whitespace-nowrap" data-id="element-573" data-mp="29srcyqiyw5l8">
            A curated collection of motion design, 3D/CGI, and visual effects built for high-end production.
          </p>
        </motion.div>

        <motion.div style={reduceMotion ? undefined : {
        opacity: cueOpacity
      }} className="absolute bottom-14 flex flex-col items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45" data-id="element-574" data-mp="5lpzjaml5147">
          Scroll to reveal the frames
          <span className="h-10 w-px bg-white/25 scroll-hint" data-id="element-575" data-mp="ya3wdn1xtre6" />
        </motion.div>
      </div>

      {/* Stage two: frames travel up over the title. */}
      <div ref={framesRef} className="relative z-10 -mt-[14svh] px-5 md:px-8" data-id="element-576" data-mp="p9p76clurs5e">
        <div className="mx-auto max-w-[1480px]" data-id="element-577" data-mp="kbe0uburnzrx">
          <div className="mb-10 flex items-center justify-between gap-4 border-b border-white/10 pb-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white/40" data-id="element-578" data-mp="kam4gytchy0l">
            <span data-id="element-579" data-mp="3513e4kftndii">{String(workItems.length).padStart(2, "0")} frames · films + motion design + visual effect reels</span>
            <span className="hidden sm:inline" data-id="element-580" data-mp="1kklr3d7bcpak">Blur resolves on scroll idle</span>
          </div>

          <div className="relative" data-id="element-581" data-mp="1pj6svh27rh14">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block" data-id="element-582" data-mp="1pg1462xhyh5x">
              {[{
              l: "24%",
              t: "13%"
            }, {
              l: "58%",
              t: "8%"
            }, {
              l: "88%",
              t: "30%"
            }, {
              l: "14%",
              t: "46%"
            }, {
              l: "48%",
              t: "60%"
            }, {
              l: "76%",
              t: "76%"
            }, {
              l: "30%",
              t: "90%"
            }].map(mark => <span key={`${mark.l}-${mark.t}`} className="crosshair absolute h-3 w-3" style={{
              left: mark.l,
              top: mark.t
            }} data-id="element-583" data-mp="287brka40kbt2" />)}
            </div>

            {/* Columns keep every gap identical while the offsets preserve the scattered rhythm. */}
            <div className="relative grid grid-cols-1 items-start gap-12 lg:grid-cols-3 lg:gap-10 xl:gap-14" data-id="element-584" data-mp="iugmlqdv1i43">
              {columns.map((column, columnIndex) => <div key={columnIndex} className={`flex flex-col gap-12 lg:gap-16 ${columnOffsets[columnIndex]}`} data-id="element-585" data-mp="mxdbfytzuagh" data-mpi={columnIndex}>
                  {column.map(item => <CollageItem key={item.id} item={item} index={workItems.indexOf(item)} progress={driftProgress} scrolling={scrolling} onOpen={onOpen} data-id="element-586" data-mp="3v4tg6vp9cdjb" />)}
                </div>)}
            </div>
          </div>
        </div>
      </div>

    </div>;
}